import { useSelector } from "react-redux";

import {
  useGetPurchaseDetailsQuery,
  useGetIndividualQuery,
} from "../../../../store/Company/companyApiSlice";
import { profileStatus, WARNING, LISTING } from "./profileStatus";

/* Who may open tabs 02, 03 and 04. */

export const PRIVILEGED_ROLES = ["ED", "AD", "CL"];

/* Tab 01 has its OWN gate, which is not the one below. */
export const canOpenGeneralInfo = (company, purchaseSubScriptionValid) =>
  company?.claimed === true || Boolean(purchaseSubScriptionValid);

/* Transcribed: only a listing or a draft is offerable. A record that is neither
   — and not claimed either — renders nothing on the live screen. */
export const generalInfoOfferable = (company) => {
  const status = profileStatus(company);
  return status === WARNING || status === LISTING;
};

/* Whether the Documents tab exists at all. */
export const showDocumentsTab = (company) =>
  Boolean(
    !String(company?.companyID || "").startsWith("TRX") ||
      company?.verifiedCompany ||
      company?.claimed
  );

export const canOpenGatedTabs = ({
  isLoggedIn,
  trevexId,
  companyExists,
  isClaimedByUser,
  role,
  purchaseSubScriptionValid,
}) => {
  if (!isLoggedIn) return false;
  if (trevexId && (companyExists || isClaimedByUser)) return true;
  if (PRIVILEGED_ROLES.includes(role)) return true;
  return Boolean(purchaseSubScriptionValid);
};

/* Transcribed from Tabs.jsx's effect: the purchase covers THIS company when
 * the ids match. An enterprise purchase only counts once it is ORDERED.
 */
export const purchaseCoversCompany = (company, purchaseDetails) => {
  if (!company || !purchaseDetails) return false;
  const companyID = company?.companyID || company?.company?.win;
  const purchaseID =
    purchaseDetails?.purchase?.companyID ||
    (purchaseDetails?.enterprise?.status === "ORDERED"
      ? purchaseDetails?.enterprise?.companyID
      : null);
  return Boolean(companyID) && companyID === purchaseID;
};

/* Transcribed from Tabs.jsx: claimed BY THIS VIEWER, read off the individual's request list — and note it checks `claimed === true` on the found row, which... */
export const claimedByViewer = (individualData, company) => {
  const found = individualData?.individual?.[0]?.requestIndividual?.find(
    (i) => i?.companyID?.companyID === company?.companyID
  );
  return found?.companyID?.claimed === true;
};

/* Transcribed: the "Get the business" prompt's wording depends on whether the
 * record came from secondary sources. */
export const isSecondaryOrListed = (company) =>
  !company?.verifiedCompany && !company?.claimed;

export const GET_DETAILS_COPY = {
  secondary:
    "The request is sourced from secondary sources and will take up to 5 working days.",
  standard: "Request to get the business information and TREVEX will do that for you.",
};

export const getDetailsCopy = (company) =>
  isSecondaryOrListed(company) ? GET_DETAILS_COPY.secondary : GET_DETAILS_COPY.standard;

/* The access question, answered from the store the way the live screen answers
 * it. Both reads are the live screen's own.
 */
export const useProfileAccess = ({ company, trevexId, role, purchaseSubScriptionValid }) => {
  const { userInfo, isLoggedIn, token } = useSelector((state) => state.auth);
  const individualID = userInfo?.individual?.[0]?.individualID;

  const { data: purchaseDetails } = useGetPurchaseDetailsQuery({
    companyID: company?.companyID || company?.company?.win,
    userID: userInfo?.individual?.[0]?.userID,
    token,
  });

  const { data: individualData } = useGetIndividualQuery(
    { individualID, token },
    { skip: !individualID }
  );

  const companyExists = purchaseCoversCompany(company, purchaseDetails);
  const isClaimedByUser = claimedByViewer(individualData, company);

  return {
    companyExists,
    isClaimedByUser,
    canOpen: canOpenGatedTabs({
      isLoggedIn,
      trevexId,
      companyExists,
      isClaimedByUser,
      role,
      purchaseSubScriptionValid,
    }),
  };
};

export default useProfileAccess;
